import { useMemo, useState } from "react";
import type { ApiError } from "@/lib/api";
import { errorMessage } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/Button";
import type { Player } from "@/lib/types";
import { CoachModal } from "./CoachModal";
import { useCoachUsers, type RosterMutations } from "../hooks/useRoster";
import { TkticSelect } from "@/components/ui/TkticSelect";

/**
 * Link-account modal — Coach > Roster.
 *
 * Ports the legacy `openCoachLinkModal` (`js/coaching/roster.js`).
 * Links a team user account to a roster player so the player's
 * feedback reaches that account (a guardian, the player's own
 * account, or another related account).
 *
 * `useCoachUsers` (gated open here) loads the team's linkable
 * users; the picker excludes accounts already linked to THIS
 * player so a coach can't double-link the same user. The
 * relationship enum mirrors `PlayerUserLink.relationship` —
 * `guardian` / `self` / `other`; the backend's
 * `CreatePlayerUserLinkRequest` validates against
 * `_VALID_PLAYER_RELATIONSHIPS`.
 */

const RELATIONSHIP_OPTIONS: ReadonlyArray<{
  value: "guardian" | "self" | "other";
  label: string;
}> = [
  { value: "guardian", label: "Guardian / family" },
  { value: "self", label: "The player's own account" },
  { value: "other", label: "Other" },
];

export type LinkAccountModalProps = {
  player: Player;
  mutations: RosterMutations;
  onClose: () => void;
  onSaved?: () => void;
};

export function LinkAccountModal({
  player,
  mutations,
  onClose,
  onSaved,
}: LinkAccountModalProps) {
  const toast = useToast();
  // Fetch the team's linkable users only while this modal is open.
  const usersQuery = useCoachUsers(true);
  const [userId, setUserId] = useState("");
  const [relationship, setRelationship] = useState<
    "guardian" | "self" | "other"
  >("guardian");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Exclude accounts already linked to this player.
  const linkedUserIds = useMemo(
    () => new Set((player.links ?? []).map((l) => l.user_id)),
    [player.links],
  );
  const availableUsers = useMemo(
    () =>
      (usersQuery.data?.users ?? []).filter((u) => !linkedUserIds.has(u.id)),
    [usersQuery.data, linkedUserIds],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) {
      setError("Pick an account to link.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await mutations.linkAccount({
        player_id: player.id,
        user_id: userId,
        relationship,
      });
      toast.success("Account linked");
      onSaved?.();
      onClose();
    } catch (err) {
      const apiErr = err as ApiError;
      setError(
        typeof apiErr?.detail === "string"
          ? apiErr.detail
          : "Could not link the account.",
      );
      toast.error("Couldn't link account", { description: errorMessage(err) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CoachModal
      title={`Link an account — ${player.display_name}`}
      onClose={onClose}
      data-testid="roster-link-modal"
    >
      <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
            Account
          </span>
          <TkticSelect
            value={userId}
            onChange={(next) => setUserId(next)}
            data-testid="link-modal-user"
            disabled={usersQuery.isLoading}
            ariaLabel="Account"
            options={[
              {
                value: "",
                label: usersQuery.isLoading
                  ? "Loading accounts…"
                  : availableUsers.length === 0
                    ? "No linkable accounts"
                    : "Select an account…",
              },
              ...availableUsers.map((u) => ({
                value: u.id,
                label: `${u.display_name} · @${u.username}`,
              })),
            ]}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
            Relationship
          </span>
          <TkticSelect
            value={relationship}
            onChange={(next) =>
              setRelationship(next as "guardian" | "self" | "other")
            }
            data-testid="link-modal-relationship"
            ariaLabel="Relationship"
            options={RELATIONSHIP_OPTIONS.map((r) => ({
              value: r.value,
              label: r.label,
            }))}
          />
        </label>

        {error ? (
          <p
            className="text-destructive font-mono text-xs"
            data-testid="link-modal-error"
          >
            {error}
          </p>
        ) : null}

        <Button
          type="submit"
          disabled={submitting || availableUsers.length === 0}
          data-testid="link-modal-submit"
        >
          {submitting ? "Linking…" : "Link account"}
        </Button>
      </form>
    </CoachModal>
  );
}
